import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

/* ---------------- helpers ---------------- */
const money = (n, currency = "GBP") => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "£ 0.00";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(v);
  } catch {
    return `£ ${v.toFixed(2)}`;
  }
};

function formatDate(value) {
  if (!value) return "-";
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${d}-${m}-${y}`;
  }
  const dt = new Date(s);
  if (!isNaN(dt)) {
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yyyy = dt.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s;
}

function StatusBadge({ paid }) {
  const bg = paid ? "var(--badge-accepted-bg, #dcfce7)" : "var(--badge-draft-bg, #f3f4f6)";
  const fg = paid ? "var(--badge-accepted-fg, #15803d)" : "var(--badge-draft-fg, #374151)";
  const br = paid ? "var(--badge-accepted-br, #bbf7d0)" : "var(--badge-draft-br, #e5e7eb)";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 8px",
        borderRadius: 999,
        fontSize: 12,
        background: bg,
        color: fg,
        border: `1px solid ${br}`,
        whiteSpace: "nowrap",
      }}
    >
      {paid ? "paid" : "unpaid"}
    </span>
  );
}

// --- Email templates (backend-first, localStorage fallback) ---
const LS_KEYS = { invoice: "jwcs.emailTemplate.invoice" };
const DEFAULTS = {
  invoice: `Hello,

Please find attached your invoice {{number}}.

Kind regards,`,
};

async function loadEmailTemplate(type) {
  try {
    const res = await fetch(`/api/email-templates?type=${encodeURIComponent(type)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return (json && json.message) || DEFAULTS[type];
  } catch {
    return localStorage.getItem(LS_KEYS[type]) || DEFAULTS[type];
  }
}

function mergePlaceholders(template, ctx) {
  return String(template || "")
    .replace(/\{\{number\}\}/g, ctx.number || "")
    .replace(/\{\{customer\}\}/g, ctx.customer || "")
    .replace(/\{\{company\}\}/g, ctx.company || "");
}

/* ---------------- component ---------------- */
export default function InvoicesPage() {
  const navigate = useNavigate();

  // filters / paging
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // selection
  const [selected, setSelected] = useState(() => new Set());
  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const selectedCount = selectedIds.length;
  const canSingleAct = selectedCount === 1;

  // pdf modal
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(undefined);
  const [pdfName, setPdfName] = useState("invoice.pdf");
  const [numPages, setNumPages] = useState(null);
  const [pdfErr, setPdfErr] = useState("");

  // email
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");

  // delete confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmRow, setConfirmRow] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const confirmNoRef = useRef(null);

  // responsive PDF width
  const pdfContainerRef = useRef(null);
  const [pdfWidth, setPdfWidth] = useState(900);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / pageSize)),
    [total, pageSize]
  );

  const fetchInvoices = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status && status !== "all") params.set("status", status);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));

    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`/api/invoices?${params.toString()}`);
      if (!res.ok) throw new Error((await res.text().catch(() => "")) || `HTTP ${res.status}`);
      const json = await res.json();

      const mapped = (json.data || []).map((r) => ({
        id: r.id,
        number: r.invoice_number,
        customer: `${r.first_name || ""} ${r.surname || ""}`.trim() || "-",
        date: r.created_at,
        paid: Number(r.paid) === 1,
        total: Number(r.total),
        pdfUrl: `/api/invoices/${r.id}/pdf`,
      }));

      setRows(mapped);
      setTotal(Number(json.total || mapped.length));

      // trim selection to visible (since API is paged)
      setSelected((prev) => {
        const next = new Set(prev);
        const visible = new Set(mapped.map((r) => r.id));
        for (const id of Array.from(next)) if (!visible.has(id)) next.delete(id);
        return next;
      });
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [q, status, from, to, page, pageSize]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // resize PDF pages to fit modal width
  useEffect(() => {
    if (!open) return;

    function updateWidth() {
      if (pdfContainerRef.current) {
        const w = pdfContainerRef.current.clientWidth;
        setPdfWidth(w > 0 ? w : 900);
      }
    }

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [open]);

  async function openPdf(url, name) {
    if (!url) return;
    setPdfErr("");
    setNumPages(null);
    setPdfName(name || "invoice.pdf");
    setOpen(true);

    if (pdfUrl && String(pdfUrl).startsWith("blob:")) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(undefined);
    }

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error((await res.text().catch(() => "")) || `HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      setPdfUrl(objUrl);
    } catch (e) {
      setPdfErr(String(e.message || e));
    }
  }

  function downloadCurrentPdf() {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = pdfName || "invoice.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function togglePaid(row) {
    try {
      const res = await fetch(`/api/invoices/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paid: row.paid ? 0 : 1 }),
      });
      if (!res.ok) throw new Error((await res.text().catch(() => "")) || `HTTP ${res.status}`);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, paid: !row.paid } : r)));
    } catch (e) {
      alert(`Failed to update: ${e.message || e}`);
    }
  }

  async function emailInvoice(row) {
    if (!row) return;
    setCurrentInvoice(row);
    setEmailSending(true);
    setEmailMsg("");

    try {
      const customer = row.customer || "";
      const company =
        document?.querySelector('meta[name="app-company"]')?.getAttribute("content") || "";

      const template = await loadEmailTemplate("invoice");
      const message = mergePlaceholders(template, {
        number: row.number,
        customer,
        company,
      });

      const res = await fetch(`/api/invoices/${row.id}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: `Invoice ${row.number}`,
          message,
        }),
      });

      if (!res.ok) throw new Error((await res.text().catch(() => "")) || `HTTP ${res.status}`);

      setEmailMsg("Email sent ✓");
      setTimeout(() => setEmailMsg(""), 4000);
    } catch (e) {
      setEmailMsg(`Failed: ${e.message || e}`);
    } finally {
      setEmailSending(false);
    }
  }

  function requestDelete(row) {
    setConfirmRow(row);
    setConfirmOpen(true);
    setTimeout(() => confirmNoRef.current?.focus(), 0);
  }

  async function deleteInvoice(row) {
    if (!row?.id) return;

    setDeletingId(row.id);

    const attempts = [
      { url: `/api/invoices/${row.id}`, options: { method: "DELETE" } },
      { url: `/api/invoices/delete?id=${encodeURIComponent(row.id)}`, options: { method: "POST" } },
      { url: `/api/invoices/${row.id}/delete`, options: { method: "POST" } },
      { url: `/api/invoices/delete/${row.id}`, options: { method: "POST" } },
    ];

    let lastErrText = "";
    let success = false;

    for (const a of attempts) {
      try {
        const res = await fetch(a.url, a.options);
        if (res.ok) {
          success = true;
          break;
        }
        lastErrText = (await res.text().catch(() => "")) || `HTTP ${res.status}`;
        if (res.status === 404 || res.status === 405) continue;
        break;
      } catch (e) {
        lastErrText = e?.message || String(e);
        continue;
      }
    }

    if (!success) {
      alert(`Failed to delete invoice: ${lastErrText || "No delete endpoint available"}`);
      setDeletingId(null);
      setConfirmOpen(false);
      setConfirmRow(null);
      return;
    }

    setRows((prev) => prev.filter((r) => r.id !== row.id));
    setTotal((t) => Math.max(0, (t || 0) - 1));
    setDeletingId(null);
    setConfirmOpen(false);
    setConfirmRow(null);

    setTimeout(() => {
      if (rows.length === 1 && page > 1) setPage((p) => p - 1);
    }, 0);
  }

  // selection helpers
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someOnPageSelected = rows.some((r) => selected.has(r.id)) && !allOnPageSelected;

  function pickOne() {
    if (!canSingleAct) return null;
    const id = selectedIds[0];
    return rows.find((r) => r.id === id) || null;
  }

  function handleTopView() {
    const row = pickOne();
    if (row) {
      setCurrentInvoice(row);
      openPdf(row.pdfUrl, `invoice-${row.number}.pdf`);
    }
  }

  function handleTopPdf() {
    const row = pickOne();
    if (row?.pdfUrl) window.open(row.pdfUrl, "_blank", "noopener,noreferrer");
  }

  function handleTopEmail() {
    const row = pickOne();
    if (row) emailInvoice(row);
  }

  function handleTopTogglePaid() {
    const row = pickOne();
    if (row) togglePaid(row);
  }

  function handleTopDelete() {
    const row = pickOne();
    if (row) requestDelete(row);
  }

  return (
    <div className="inv-fullbleed">
      <style>{`
        /* --- Full-bleed wrapper (MATCH CustomerForm) --- */
        .inv-fullbleed{
          min-height: 1px;
          padding: 16px clamp(12px, 2vw, 24px);
          position: relative;
          left: 50%;
          right: 50%;
          margin-left: calc(50% - 50vw);
          margin-right: calc(50% - 50vw);
          width: 100vw;
          max-width: 100vw;
          background: var(--bg, #f8fafc);
          color: var(--text, #111827);
          box-sizing: border-box;
          overflow-x: hidden; /* crucial: stop page-level sideways bleed */
        }

        /* Kill the full-bleed math on small screens to avoid a ~50% left gap */
        @media (max-width: 640px){
          .inv-fullbleed{
            left: auto;
            right: auto;
            margin-left: 0;
            margin-right: 0;
            width: 100%;
            max-width: 100%;
          }
        }

        /* Force card to fill width (prevents "narrow centered card" rules) */
        .inv-fullbleed .card{
          width: 100%;
          max-width: 100% !important;
          margin: 0 auto !important;
          min-width: 0;
          box-sizing: border-box;
          overflow: hidden;
        }

        /* --- Sections --- */
        .inv-header{
          padding: 10px 12px;
          border-bottom: 1px solid var(--border);
          font-weight: 700;
        }
        .inv-body{ padding: 12px; }

        /* --- Top actions --- */
        .inv-actions{
          display:flex;
          flex-wrap:wrap;
          align-items:center;
          gap:10px;
          margin-bottom: 12px;
          min-width: 0;
        }
        .inv-actions .muted{ margin-left:auto; opacity: .8; }

        /* --- Filters --- */
        .inv-filters{
          margin: 12px 0;
          border: 1px solid var(--border);
          background: var(--card);
          border-radius: 12px;
          overflow: hidden;
        }
        .inv-filters__header{
          padding: 10px 12px;
          border-bottom: 1px solid var(--border);
          font-weight: 600;
        }
        .inv-filters__body{ padding: 12px; }

        .inv-grid{
          display:grid;
          gap:10px;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        }

        /* --- Table wrapper (desktop/tablet) --- */
        .inv-table-wrap{
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          border: 1px solid var(--border);
          border-radius: 12px;
          background: var(--card);
          box-sizing: border-box;
        }

        .inv-table{
          width: 100%;
          border-collapse: collapse;
          min-width: 860px; /* desktop/tablet baseline */
        }
        .inv-table th, .inv-table td{
          padding: 10px 12px;
          border-bottom: 1px solid var(--border);
          vertical-align: top;
          white-space: nowrap;
        }
        .inv-table thead th{ text-align:left; font-weight:600; }

        .inv-col-total, .inv-col-status{ text-align:right; }
        .inv-col-select{ width: 44px; }
        .inv-col-number{ min-width: 150px; }
        .inv-col-customer{ min-width: 260px; white-space: normal; }
        .inv-col-date{ min-width: 140px; }
        .inv-col-total{ min-width: 140px; }
        .inv-col-status{ min-width: 120px; }

        .inv-pagination{
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap:10px;
          margin-top: 10px;
          flex-wrap: wrap;
        }
        .inv-pagination__controls{
          display:flex;
          align-items:center;
          gap:8px;
          flex-wrap: nowrap;
        }
        .inv-pagination__page{ font-size: 12px; white-space: nowrap; }

        /* --- Mobile: stack rows into cards (same idea as SR page) --- */
        @media (max-width: 720px){
          .inv-actions{
            display:grid;
            grid-template-columns: 1fr 1fr;
            gap:10px;
            align-items:stretch;
          }
          .inv-actions .btn{ width:100%; }
          .inv-actions .btn.secondary:last-of-type{ grid-column: 1 / -1; }
          .inv-actions .muted{
            grid-column: 1 / -1;
            justify-self:end;
            margin-left: 0;
          }

          .inv-grid{ grid-template-columns: 1fr; }

          /* stop horizontal table logic on mobile */
          .inv-table-wrap{
            overflow-x: hidden;
            border: none;
            background: transparent;
          }
          .inv-table{ min-width: 0 !important; }
          .inv-table thead{ display:none; }

          .inv-table, .inv-table tbody, .inv-table tr, .inv-table td{
            display:block;
            width:100%;
          }

          .inv-table tr{
            background: var(--card);
            border: 1px solid var(--border);
            border-radius: 12px;
            padding: 10px 12px;
            margin-bottom: 12px;
            overflow: hidden;
          }

          .inv-table td{
            border: none;
            padding: 6px 0;
            display:flex;
            justify-content: space-between;
            gap: 12px;
            min-width: 0;
            white-space: normal;
          }

          .inv-table td::before{
            content: attr(data-label);
            color: var(--muted);
            font-size: 12px;
            flex: 0 0 auto;
          }

          .inv-table td.inv-col-select{
            justify-content:flex-start;
            padding-bottom: 8px;
          }
          .inv-table td.inv-col-select::before{ content: ""; }

          .inv-col-total, .inv-col-status{ text-align:left; }
        }
      `}</style>

      <div className="card">
        <div className="inv-header">Invoices</div>

        <div className="inv-body">
          {err && (
            <div
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #fecaca",
                background: "#fee2e2",
                color: "#991b1b",
              }}
            >
              {err}
            </div>
          )}

          {/* Top actions */}
          <div className="inv-actions">
            <button className="btn" onClick={handleTopView} disabled={!canSingleAct}>
              View
            </button>
            <button className="btn" onClick={handleTopPdf} disabled={!canSingleAct}>
              PDF
            </button>
            <button
              className="btn"
              onClick={handleTopEmail}
              disabled={!canSingleAct || (emailSending && currentInvoice?.id === selectedIds[0])}
            >
              {emailSending && canSingleAct && currentInvoice?.id === selectedIds[0] ? "Sending…" : "Email"}
            </button>
            <button className="btn" onClick={() => navigate("/calendar")}>
              Calendar
            </button>
            <button className="btn" onClick={handleTopTogglePaid} disabled={!canSingleAct}>
              Mark Paid / Unpaid
            </button>
            <button
              className="btn"
              onClick={handleTopDelete}
              disabled={!canSingleAct || deletingId === selectedIds[0]}
              style={{ background: "#991b1b", border: "1px solid #991b1b", color: "#fee2e2" }}
            >
              {deletingId === selectedIds[0] ? "Deleting…" : "Delete"}
            </button>
            <button className="btn secondary" onClick={() => navigate("/quotes")}>
              Back to Quotes
            </button>

            <div className="muted">
              {selectedCount > 0 ? `${selectedCount} selected` : "No invoices selected"}
            </div>
          </div>

          {/* Filters */}
          <div className="inv-filters">
            <div className="inv-filters__header">Filters</div>
            <div className="inv-filters__body">
              <div className="inv-grid">
                <div>
                  <label className="label">Search (number or customer)</label>
                  <input
                    className="input"
                    placeholder="e.g. I10000013 or Jane Doe"
                    value={q}
                    onChange={(e) => {
                      setPage(1);
                      setQ(e.target.value);
                    }}
                  />
                </div>

                <div>
                  <label className="label">Status</label>
                  <select
                    className="input"
                    value={status}
                    onChange={(e) => {
                      setPage(1);
                      setStatus(e.target.value);
                    }}
                  >
                    <option value="all">All</option>
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                  </select>
                </div>

                <div>
                  <label className="label">From</label>
                  <input
                    className="input"
                    type="date"
                    value={from}
                    onChange={(e) => {
                      setPage(1);
                      setFrom(e.target.value);
                    }}
                  />
                </div>

                <div>
                  <label className="label">To</label>
                  <input
                    className="input"
                    type="date"
                    value={to}
                    onChange={(e) => {
                      setPage(1);
                      setTo(e.target.value);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table / Mobile Cards */}
          <div className="inv-table-wrap">
            <table className="inv-table">
              <thead>
                <tr>
                  <th className="inv-col-select">
                    <input
                      type="checkbox"
                      aria-label="Select all on page"
                      checked={allOnPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someOnPageSelected;
                      }}
                      onChange={(e) => {
                        setSelected((prev) => {
                          const next = new Set(prev);
                          for (const r of rows) e.target.checked ? next.add(r.id) : next.delete(r.id);
                          return next;
                        });
                      }}
                    />
                  </th>
                  <th className="inv-col-number">Invoice #</th>
                  <th className="inv-col-customer">Customer</th>
                  <th className="inv-col-date">Date</th>
                  <th className="inv-col-total">Total</th>
                  <th className="inv-col-status">Status</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="muted" style={{ padding: 16, textAlign: "center" }}>
                      Loading…
                    </td>
                  </tr>
                )}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="muted" style={{ padding: 16, textAlign: "center" }}>
                      No invoices found.
                    </td>
                  </tr>
                )}

                {!loading &&
                  rows.map((r) => (
                    <tr key={r.id}>
                      <td className="inv-col-select" data-label="">
                        <input
                          type="checkbox"
                          aria-label={`Select invoice ${r.number}`}
                          checked={selected.has(r.id)}
                          onChange={() =>
                            setSelected((prev) => {
                              const next = new Set(prev);
                              if (next.has(r.id)) next.delete(r.id);
                              else next.add(r.id);
                              return next;
                            })
                          }
                        />
                      </td>

                      <td className="inv-col-number" data-label="Invoice #" style={{ fontWeight: 600 }}>
                        #{r.number}
                      </td>

                      <td className="inv-col-customer" data-label="Customer">
                        {r.customer || "-"}
                      </td>

                      <td className="inv-col-date" data-label="Date">
                        {formatDate(r.date)}
                      </td>

                      <td className="inv-col-total" data-label="Total">
                        {money(r.total)}
                      </td>

                      <td className="inv-col-status" data-label="Status">
                        <StatusBadge paid={r.paid} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="inv-pagination">
            <div className="muted inv-pagination__summary">
              {(total || 0).toLocaleString()} result{total === 1 ? "" : "s"}
            </div>
            <div className="inv-pagination__controls">
              <button
                className="btn secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              <div className="muted inv-pagination__page">
                Page {page} / {totalPages}
              </div>
              <button
                className="btn secondary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>

          {/* PDF Modal */}
          {open && (
            <div
              onClick={() => setOpen(false)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                zIndex: 1000,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "min(1000px, 96vw)",
                  maxHeight: "90vh",
                  background: "var(--card)",
                  color: "var(--text)",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  boxShadow: "var(--shadow)",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", fontWeight: 600 }}>
                  Invoice PDF Preview
                </div>

                <div ref={pdfContainerRef} style={{ padding: 16, overflowY: "auto", overflowX: "hidden" }}>
                  {pdfErr ? (
                    <div
                      style={{
                        padding: 16,
                        borderRadius: 8,
                        border: "1px solid #fecaca",
                        background: "#fee2e2",
                        color: "#991b1b",
                      }}
                    >
                      Failed to load PDF: {pdfErr}
                    </div>
                  ) : !pdfUrl ? (
                    <div className="muted" style={{ padding: 24, textAlign: "center" }}>
                      Loading PDF…
                    </div>
                  ) : (
                    <Document
                      file={pdfUrl}
                      onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                      onLoadError={(e) => setPdfErr(String(e?.message || e))}
                      onSourceError={(e) => setPdfErr(String(e?.message || e))}
                      loading={
                        <div className="muted" style={{ padding: 24, textAlign: "center" }}>
                          Loading PDF…
                        </div>
                      }
                    >
                      {Array.from(new Array(numPages || 0), (_, idx) => (
                        <Page key={`p_${idx + 1}`} pageNumber={idx + 1} width={pdfWidth} />
                      ))}
                    </Document>
                  )}
                </div>

                <div
                  style={{
                    padding: 12,
                    borderTop: "1px solid var(--border)",
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {pdfUrl && (
                    <>
                      <button className="btn" onClick={downloadCurrentPdf}>
                        Download
                      </button>
                      <button
                        className="btn"
                        onClick={() => currentInvoice && emailInvoice(currentInvoice)}
                        disabled={emailSending || !currentInvoice}
                      >
                        {emailSending ? "Sending…" : "Email to Customer"}
                      </button>
                      {emailMsg && (
                        <span
                          style={{
                            fontSize: 12,
                            color: emailMsg.startsWith("Email sent") ? "#15803d" : "#b91c1c",
                            marginRight: "auto",
                          }}
                        >
                          {emailMsg}
                        </span>
                      )}
                    </>
                  )}
                  <button className="btn secondary" onClick={() => setOpen(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Confirm Delete Modal */}
          {confirmOpen && (
            <div
              onClick={() => !deletingId && setConfirmOpen(false)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                zIndex: 1100,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirm-title"
                style={{
                  width: "min(520px, 96vw)",
                  background: "var(--card)",
                  color: "var(--text)",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  boxShadow: "var(--shadow)",
                }}
              >
                <div
                  id="confirm-title"
                  style={{
                    padding: "14px 18px",
                    borderBottom: "1px solid var(--border)",
                    fontWeight: 700,
                  }}
                >
                  Confirm delete
                </div>
                <div style={{ padding: 18 }}>
                  {confirmRow ? (
                    <div>
                      Delete invoice <strong>#{confirmRow.number}</strong> (ID {confirmRow.id})? This cannot be undone.
                    </div>
                  ) : (
                    <div>Delete this invoice? This cannot be undone.</div>
                  )}
                </div>
                <div
                  style={{
                    padding: 12,
                    borderTop: "1px solid var(--border)",
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    ref={confirmNoRef}
                    className="btn secondary"
                    onClick={() => setConfirmOpen(false)}
                    disabled={!!deletingId}
                  >
                    No
                  </button>
                  <button
                    className="btn"
                    onClick={() => confirmRow && deleteInvoice(confirmRow)}
                    disabled={!!deletingId}
                    style={{ background: "#fee2e2", border: "1px solid #fecaca", color: "#991b1b" }}
                  >
                    {deletingId ? "Deleting…" : "Yes, delete"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Email feedback line */}
          {emailMsg && (
            <div
              style={{
                marginTop: 10,
                fontSize: 12,
                color: emailMsg.startsWith("Email sent") ? "#15803d" : "#b91c1c",
              }}
            >
              {emailMsg}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}